import React, { useEffect, useState, useRef } from 'react';
import { normalizeSearch } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { EventItem, Place } from '../../types/shared/event';
import { useHeader } from '../../context/HeaderContext';
import { SearchAndFilterBar } from '../standard/SearchAndFilterBar';
import { FilterDrawer } from '../shared/FilterDrawer';
import type { FilterState } from '../shared/FilterDrawer';
import { ListView } from './ListView';
import { MapView } from './MapView';
import { ViewToggle } from '../shared/ViewToggle';
import { DetailCard } from './DetailCard';
import { PlaceCard } from './PlaceCard';
import { DateRangeSelector } from './DateRangeSelector';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useAirtableQuery } from '../../hooks/useAirtableQuery';
import { useTicketmasterQuery } from '../../hooks/useTicketmasterQuery';
import type { SupportedLanguages } from '../../i18n';
import { getEventAdapter } from '../../lib/adapters';
import { slugify } from '../../utils/slugify';
import { SectionHeader } from '../layout/SectionHeader';
import { resolveColor } from '../../utils/colorUtils';
import { useSection } from '../../hooks/useSection';
import { stayService } from '../../lib/stay/stayService';
import { ticketmasterCache } from '../../lib/ticketmaster';
import { navigationClient } from '../../lib/navigation';
import { useAnalytics } from '../../hooks/useAnalytics';
import ErrorBoundary from '../analytics/ErrorBoundary';

const EventApiSection: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const section = useSection();
  const scrollPositionRef = useRef<number>(0);
  
  // Initialize analytics with section context
  const analytics = useAnalytics({
    sectionId: section?.id,
    sectionName: section?.names?.fr,
    sectionType: 'event-api'
  });
  
  // Refs
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const lastStayDatesRef = useRef<string | null>(null);
  
  // State
  const [filters, setFilters] = useState<FilterState>({ category: '', categories: [], types: [], priceFilter: [] });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
  const [view, setView] = useState<'map' | 'list'>('list');
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [detailBehavior, setDetailBehavior] = useState<'default' | 'pin' | 'place'>('default');
  const [selectedShowIndex, setSelectedShowIndex] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchParams, setSearchParams] = useState<{
    startDateTime?: string;
    endDateTime?: string;
  } | null>(null);
  const [isDateRangeSelectorModalOpen, setIsDateRangeSelectorModalOpen] = useState(true);
  const [datesInitialized, setDatesInitialized] = useState(false);
  const [areDatesExpired, setAreDatesExpired] = useState(false);

  // Data fetching hooks
  const { data: config, isLoading: configLoading } = useAirtableQuery.useConfig();
  const { data: translations, isLoading: translationsLoading } = useAirtableQuery.useTranslations(i18n.language as SupportedLanguages);
  
  // Load Ticketmaster config
  const { data: tmConfig, isLoading: tmConfigLoading } = useTicketmasterQuery();

  // Load events
  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['ticketmaster-events', tmConfig, searchParams],
    queryFn: async () => {
      if (!tmConfig || !searchParams) return [];

      try {
        const adapter = getEventAdapter('ticketmaster');
        if (!adapter) {
          throw new Error('Ticketmaster adapter not found');
        }

        const result = await adapter.getEvents(tmConfig, searchParams);
        return result;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!tmConfig && !!searchParams && !areDatesExpired,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  // Track section view
  useEffect(() => {
    if (section?.id) {
      analytics.trackSectionView(view);
    }
  }, [section?.id, view, analytics]);

  // Check for stay date changes on component mount and focus
  useEffect(() => {
    // Function to check if stay dates have changed
    const checkStayDatesChanged = () => {
      const currentStayDates = stayService.getStayDates();
      const currentStayDatesJson = currentStayDates ? JSON.stringify(currentStayDates) : null;
      
      // If we have stay dates and they're different from what we had before
      if (currentStayDates && currentStayDatesJson !== lastStayDatesRef.current) {
        // Update our reference
        lastStayDatesRef.current = currentStayDatesJson;
        
        // Check if dates are expired
        const datesExpired = stayService.areDatesExpired();
        setAreDatesExpired(datesExpired);
        
        if (!datesExpired) {
          // Clear cache for previous date range if it exists
          if (searchParams?.startDateTime && searchParams?.endDateTime) {
            ticketmasterCache.clearCacheForDateRange(
              searchParams.startDateTime,
              searchParams.endDateTime
            );
          }
          
          // Update search params with new dates
          setSearchParams({
            startDateTime: currentStayDates.startDate,
            endDateTime: currentStayDates.endDate
          });
          
          // Update local state
          setStartDate(new Date(currentStayDates.startDate));
          setEndDate(new Date(currentStayDates.endDate));
          
          // Close the date selector modal
          setIsDateRangeSelectorModalOpen(false);
        } else {
          // If dates are expired, clear search params and show date selector
          setSearchParams(null);
          setIsDateRangeSelectorModalOpen(true);
        }
      }
    };
    
    // Check on mount
    checkStayDatesChanged();
    
    // Also check when window gets focus (user might have changed dates in another tab/section)
    const handleFocus = () => {
      checkStayDatesChanged();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [searchParams]);

  // Memoized values
  const categoryColors = React.useMemo(() => {
    if (!config || !events.length) return new Map<string, string>();

    const colors = config.colors_map.split(',').map((color: string) => color.trim());
    const uniqueCategories = Array.from(new Set(events.map(event => event.category)));
    const colorMap = new Map<string, string>();

    uniqueCategories.forEach((category, index) => {
      colorMap.set(category, colors[index % colors.length]);
    });

    return colorMap;
  }, [config, events]);

  const activeCategories = React.useMemo(() =>
    filters.categories.length > 0 ? filters.categories : (filters.category ? [filters.category] : []),
    [filters.categories, filters.category]
  );

  const activeFiltersCount =
    activeCategories.length + filters.types.length + filters.priceFilter.length;

  const filteredEvents = React.useMemo(() =>
    events.filter(event => {
      const categoryMatch = activeCategories.length === 0 || activeCategories.includes(event.category);
      const typeMatch = filters.types.length === 0 || filters.types.includes(event.type);
      const priceMatch = filters.priceFilter.length === 0 || filters.priceFilter.includes(event.price?.trim() || '');
      const q = normalizeSearch(searchQuery.trim());
      const searchMatch =
        !q ||
        normalizeSearch(event.name).includes(q) ||
        normalizeSearch(event.category || '').includes(q) ||
        normalizeSearch(event.type || '').includes(q);
      return categoryMatch && typeMatch && priceMatch && searchMatch;
    }),
    [events, filters, searchQuery, activeCategories]
  );

  const places = React.useMemo(() => {
    const adapter = getEventAdapter('ticketmaster');
    return adapter ? adapter.getPlaces(filteredEvents) : [];
  }, [filteredEvents]);

  const mainLocation = React.useMemo(() => ({
    name: config.name,
    latitude: config.latitude,
    longitude: config.longitude,
    fulladress: config.fulladress
  }), [config.name, config.latitude, config.longitude, config.fulladress]);

  const allCategories = React.useMemo(() =>
    Array.from(new Set(events.map(e => e.category))).filter(Boolean),
    [events]
  );

  // Effects
  useEffect(() => {
    if (section?.names) {
      const name = section.names[i18n.language as SupportedLanguages] || section.names.fr;
      setTitle(name);
    }
  }, [section, i18n.language, setTitle]);

  // Initialize dates from stay service
  useEffect(() => {
    if (datesInitialized) {
      return; // Skip if already initialized
    }

    // Check if dates are expired
    const datesExpired = stayService.areDatesExpired();
    setAreDatesExpired(datesExpired);

    // Initialize with today's date and tomorrow as default
    const today = new Date();
    setStartDate(today);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 59);
    setEndDate(tomorrow);

    // Check if we have saved stay dates
    const savedDates = stayService.getStayDates();
    
    if (savedDates && !datesExpired) {
      // Use saved dates if available and not expired
      setStartDate(new Date(savedDates.startDate));
      setEndDate(new Date(savedDates.endDate));
      
      // Store the current stay dates for change detection
      lastStayDatesRef.current = JSON.stringify(savedDates);
      
      // Auto-search with saved dates
      setSearchParams({
        startDateTime: savedDates.startDate,
        endDateTime: savedDates.endDate
      });
      
      // Close the date selector modal
      setIsDateRangeSelectorModalOpen(false);
    } else {
      // Keep the date selector modal open
      setIsDateRangeSelectorModalOpen(true);
      
      // Clear search params if dates are expired
      if (datesExpired) {
        setSearchParams(null);
      }
    }

    setDatesInitialized(true);
  }, [datesInitialized]);

  // Parse the URL parameter to find the correct event
  useEffect(() => {
    if (name && events.length > 0) {
      // Check if the name contains date and time information
      const parts = name.split('-');
      if (parts.length >= 3) {
        // The last two parts should be date and time
        const dateStr = parts[parts.length - 2];
        const timeStr = parts[parts.length - 1];
        
        // Try to find an event with matching name, date, and time
        const event = events.find(e => {
          const eventNameSlug = slugify(e.name);
          const eventDateSlug = e.date.replace(/-/g, ''); // Convert YYYY-MM-DD to YYYYMMDD
          const eventTimeSlug = e.hour.replace(':', ''); // Convert HH:MM to HHMM
          
          // Check if the base name matches (excluding date and time parts)
          const baseNameSlug = parts.slice(0, parts.length - 2).join('-');
          
          return eventNameSlug.includes(baseNameSlug) && 
                 eventDateSlug === dateStr && 
                 eventTimeSlug === timeStr;
        });
        
        if (event) {
          setSelectedEvent(event);
          
          // Track detail card view from URL navigation
          analytics.trackItemAction(event.id, 'item_click', 'event-api', {
            item_name: event.name,
            category: event.category,
            type: event.type,
            source: 'url_navigation',
            date: event.date,
            time: event.hour,
            show_id: event.showId
          });
          
          return;
        }
      }
      
      // Fallback to just matching by name if the above doesn't work
      const event = events.find(e => slugify(e.name) === name);
      if (event) {
        setSelectedEvent(event);
        
        // Track detail card view from URL navigation
        analytics.trackItemAction(event.id, 'item_click', 'event-api', {
          item_name: event.name,
          category: event.category,
          type: event.type,
          source: 'url_navigation',
          date: event.date,
          time: event.hour,
          show_id: event.showId
        });
      }
    }
  }, [name, events, analytics]);

  // Event handlers
  const handleBackClick = () => {
    analytics.trackEvent('navigation', 'menu_click', {
      target: 'home',
      section_id: section?.id,
      section_name: section?.names?.fr
    });
    
    navigate('/');
  };

  const handleSearch = () => {
    if (!startDate || !endDate) return;

    // For Ticketmaster API, we need to convert dates to UTC
    const startDateTime = new Date(startDate);
    const endDateTime = new Date(endDate);

    // If start date is today, use current time
    const now = new Date();
    if (startDateTime.toDateString() === now.toDateString()) {
      startDateTime.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
    } else {
      startDateTime.setHours(0, 0, 1);
    }

    // Make sure the end date includes the full day by setting it to 23:59:59
    endDateTime.setHours(23, 59, 59);

    // Convert to UTC ISO strings for Ticketmaster API
    const startDateTimeUTC = startDateTime.toISOString();
    const endDateTimeUTC = endDateTime.toISOString();

    // Save the dates to the stay service
    stayService.setStayDates(startDate, endDate);
    
    // Update our reference for change detection
    lastStayDatesRef.current = JSON.stringify({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      lastUpdated: new Date().toISOString()
    });
    
    // Reset the expired flag since we have new dates
    setAreDatesExpired(false);

    // Clear cache for previous date range if it exists
    if (searchParams?.startDateTime && searchParams?.endDateTime) {
      ticketmasterCache.clearCacheForDateRange(
        searchParams.startDateTime,
        searchParams.endDateTime
      );
    }

    // Set search params to trigger the query
    setSearchParams({
      startDateTime: startDateTimeUTC,
      endDateTime: endDateTimeUTC
    });

    // Close the date range selector modal
    setIsDateRangeSelectorModalOpen(false);

    // Track search event
    analytics.trackFilter('date_filter', `${startDate.toISOString()}_${endDate.toISOString()}`, {
      start_date: startDateTimeUTC,
      end_date: endDateTimeUTC
    });
  };

  const handleFiltersChange = (newFilters: FilterState) => {
    analytics.trackFilter('filter_change', JSON.stringify(newFilters));
    setFilters(newFilters);
  };

  const handleCategorySelect = (category: string) => {
    const current = activeCategories;
    const isAlready = current.includes(category);
    const next = isAlready ? current.filter(c => c !== category) : [...current, category];
    setFilters(prev => ({ ...prev, category: next.length === 1 ? next[0] : '', categories: next, types: [] }));
  };

  const handleTypeSelect = (type: string) => {
    setFilters(prev => ({ ...prev, types: type ? [type] : [] }));
  };

  const handleViewChange = (newView: 'map' | 'list') => {
    analytics.trackEvent('view', 'view_change', {
      section_id: section.id,
      section_name: section.names.fr,
      section_type: 'event-api',
      view_type: newView,
      previous_view: view
    });
    
    setView(newView);
  };

  const handleItemClick = (item: EventItem) => {
    scrollPositionRef.current = window.scrollY;

    analytics.trackItemAction(item.id, 'item_click', 'event-api', {
      item_name: item.name,
      category: item.category,
      type: item.type,
      source: view === 'list' ? 'list_view' : 'map_view',
      date: item.date,
      time: item.hour,
      show_id: item.showId
    });

    if (view === 'map' && mapInstanceRef.current && item.latitude && item.longitude) {
      mapInstanceRef.current.panTo({ lat: parseFloat(String(item.latitude)), lng: parseFloat(String(item.longitude)) });
      const currentZoom = mapInstanceRef.current.getZoom() || 12;
      if (currentZoom < 15) {
        mapInstanceRef.current.setZoom(15);
      }
    }

    handleEventClick(item);
  };

  const handleEventClick = (event: EventItem, behavior: 'default' | 'pin' = 'default') => {
    // Create a unique URL that includes date and time
    const eventUrl = createEventUrl(event);
    
    setSelectedEvent(event);
    setDetailBehavior(behavior);
    setSelectedPlace(null);
    setSelectedShowIndex(null);
    navigate(eventUrl, { replace: true });
  };

  const handlePlaceClick = (place: Place) => {
    if (!section?.names?.fr) return;

    if (place.shows.length === 1 && place.shows[0].events.length > 1) {
      analytics.trackItemAction(place.shows[0].events[0].id, 'item_click', 'event-api', {
        item_name: place.shows[0].events[0].name,
        category: place.shows[0].events[0].category,
        type: place.shows[0].events[0].type,
        source: 'map_marker',
        place_name: place.name,
        show_id: place.shows[0].showId
      });
      
      setSelectedEvent(place.shows[0].events[0]);
      setDetailBehavior('pin');
      setSelectedPlace(null);
      setSelectedShowIndex(null);
    } else {
      analytics.trackEvent('interaction', 'place_card_view', {
        section_id: section.id,
        place_name: place.name,
        shows_count: place.shows.length,
        show_ids: place.shows.map(show => show.showId || '').filter(Boolean)
      });
      
      setSelectedPlace(place);
      setSelectedEvent(null);
      setDetailBehavior('default');
      setSelectedShowIndex(null);
    }
  };

  const handleEventFromPlace = (event: EventItem, showIndex: number) => {
    if (!section?.names?.fr) return;
    
    analytics.trackItemAction(event.id, 'item_click', 'event-api', {
      item_name: event.name,
      category: event.category,
      type: event.type,
      source: 'place_card',
      place_name: selectedPlace?.name,
      show_index: showIndex,
      date: event.date,
      time: event.hour,
      show_id: event.showId
    });
    
    // Create a unique URL that includes date and time
    const eventUrl = createEventUrl(event);
    
    setSelectedEvent(event);
    setSelectedShowIndex(showIndex);
    setDetailBehavior('place');
    navigate(eventUrl, { replace: true });
  };

  // Create a unique URL for an event that includes date and time
  const createEventUrl = (event: EventItem): string => {
    if (!section?.names?.fr) return '';
    
    const sectionSlug = slugify(section.names.fr);
    const nameSlug = slugify(event.name);
    const dateSlug = event.date.replace(/-/g, ''); // Convert YYYY-MM-DD to YYYYMMDD
    const timeSlug = event.hour.replace(':', ''); // Convert HH:MM to HHMM
    
    // Use relative navigation
    return `/${sectionSlug}/${nameSlug}-${dateSlug}-${timeSlug}`;
  };

  const handleDetailClose = () => {
    // Navigate to the section without item parameter
    const sectionSlug = slugify(section.names.fr);
    navigate(`/${sectionSlug}`, { replace: true });
    
    analytics.trackEvent('interaction', 'detail_card_close', {
      section_id: section.id,
      item_id: selectedEvent?.id,
      item_name: selectedEvent?.name,
      view_duration: selectedEvent ? Date.now() - scrollPositionRef.current : 0
    });
    
    setSelectedEvent(null);
    setSelectedPlace(null);
    setSelectedShowIndex(null);
    setDetailBehavior('default');
    
    // Restore scroll position after a short delay to allow for navigation
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
    }, 50);
  };

  // Loading state
  if (configLoading || translationsLoading || tmConfigLoading) {
    return <LoadingIndicator />;
  }

  // Error state
  if (!config || !translations || !section?.names || !tmConfig) {
    analytics.trackError('section_load_error', new Error('Missing configuration'), {
      section_id: section?.id,
      section_type: 'event-api'
    });
    
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">Erreur lors du chargement des données</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary 
      componentName="EventApiSection" 
      sectionId={section.id}
      sectionName={section.names.fr}
    >
      <div 
        className="min-h-screen"
        style={{ 
          backgroundColor: resolveColor(config['sectionBack-color'], config, '#f9fafb')
        }}
      >
        <SectionHeader
          config={config}
          title={section.names[i18n.language as SupportedLanguages] || section.names.fr}
          onBackClick={handleBackClick}
          translations={translations}
        />

        {/* Filter */}
        <div className="filter-block" style={{ backgroundColor: config['major-color'] }}>
          <SearchAndFilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onFiltersOpen={() => setIsFilterDrawerOpen(true)}
            activeFiltersCount={activeFiltersCount}
            config={config}
            translations={translations}
          />
        </div>

        {/* Main content */}
        <main className="main-content">
          <div className="pt-4">
            {tmConfig && (
              <DateRangeSelector
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                onSearch={handleSearch}
                config={config}
                translations={translations}
                isModalOpen={isDateRangeSelectorModalOpen}
                setIsModalOpen={setIsDateRangeSelectorModalOpen}
              />
            )}
          </div>

          {eventsLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingIndicator />
            </div>
          ) : events.length === 0 && searchParams === null ? (
            <div 
              className="flex items-center justify-center h-64 text-gray-500 text-center"
              dangerouslySetInnerHTML={{ __html: translations.no_result_event[i18n.language] || translations.no_result_event.fr }}
            />
          ) : events.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-500 text-center">
              <p>Aucun événement disponible pour cette période</p>
            </div>
          ) : view === 'list' ? (
            <ListView
              events={filteredEvents}
              onEventClick={handleItemClick}
              onCategorySelect={handleCategorySelect}
              onTypeSelect={handleTypeSelect}
              config={config}
              translations={translations}
              categoryColors={categoryColors}
            />
          ) : (
            <div 
              className="fixed inset-0 pt-[94px] pb-[40px]"
              style={{ zIndex: 1 }}
            >
              <MapView
                places={places}
                config={config}
                translations={translations}
                onEventClick={(event, behavior) => {
                  handleEventClick(event, behavior);
                  if (behavior === 'pin') {
                    setSelectedPlace(null);
                  }
                }}
                onPlaceClick={handlePlaceClick}
                mainLocation={mainLocation}
                categoryColors={categoryColors}
                onCategorySelect={handleCategorySelect}
                onTypeSelect={handleTypeSelect}
                mapInstanceRef={mapInstanceRef}
                activeCategories={activeCategories}
                allCategories={allCategories}
              />
            </div>
          )}
        </main>

        {/* Fixed view toggle button */}
        <div 
          className="fixed left-1/2 -translate-x-1/2 z-[150] bottom-[58px]"
        >
          <ViewToggle
            view={view}
            onChange={handleViewChange}
            config={config}
            translations={translations}
            sectionType="event-api"
          />
        </div>

        {/* Modals */}
        {selectedPlace && !selectedEvent && (
          <PlaceCard
            place={selectedPlace}
            onClose={() => {
              analytics.trackEvent('interaction', 'place_card_close', {
                place_name: selectedPlace.name,
                shows_count: selectedPlace.shows.length
              });
              setSelectedPlace(null);
            }}
            onEventClick={handleEventFromPlace}
            onCategorySelect={handleCategorySelect}
            onTypeSelect={handleTypeSelect}
            config={config}
            translations={translations}
            categoryColors={categoryColors}
          />
        )}

        {selectedEvent && section?.names?.fr && (
          <DetailCard
            item={selectedEvent}
            items={filteredEvents}
            onClose={handleDetailClose}
            onItemChange={(newEvent) => {
              analytics.trackItemAction(newEvent.id, 'navigation_next', 'event-api', {
                item_name: newEvent.name,
                category: newEvent.category,
                type: newEvent.type,
                previous_item_id: selectedEvent?.id,
                previous_item_name: selectedEvent?.name,
                date: newEvent.date,
                time: newEvent.hour,
                show_id: newEvent.showId
              });
              
              // Create a unique URL that includes date and time
              const eventUrl = createEventUrl(newEvent);
              
              if (detailBehavior === 'place' && selectedPlace) {
                const newIndex = selectedPlace.shows.findIndex(show => 
                  show.events.some(e => e.id === newEvent.id)
                );
                if (newIndex !== -1) {
                  setSelectedEvent(newEvent);
                  setSelectedShowIndex(newIndex);
                }
              } else {
                setSelectedEvent(newEvent);
              }
              navigate(eventUrl, { replace: true });
            }}
            onCategorySelect={handleCategorySelect}
            onTypeSelect={handleTypeSelect}
            config={config}
            translations={translations}
            tableId={section.id}
            categoryColors={categoryColors}
            behavior={detailBehavior}
            showIndex={selectedShowIndex}
            place={selectedPlace}
            context={{
              translationTableId: tmConfig.translationTableId,
              locale: tmConfig.locale
            }}
            onShowPlace={(place) => {
              setSelectedEvent(null);
              setSelectedPlace(place);
            }}
          />
        )}

        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          items={events}
          config={config}
          translations={translations}
          categoryColors={categoryColors}
        />
      </div>
    </ErrorBoundary>
  );
};

export default EventApiSection;
export { EventApiSection };